import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LuCheck, LuDownload } from "react-icons/lu";
import Page from "../../components/page/Page";
import { createInventoryTransaction, getOrderReceipt } from "../../services/OnlineStore";
import styles from "./PaymentSuccess.module.css";

const pageClasses = {
  container: styles.page,
  content: styles.pageContent,
  contentSkinny: styles.pageContent,
  title: styles.pageTitle,
};

function formatCurrency(value) {
  return `$${((Number(value) || 0) / 100).toFixed(2)}`;
}

function readPaymentData() {
  try {
    return JSON.parse(sessionStorage.getItem("paymentData")) || null;
  } catch (error) {
    console.error("Failed to parse payment data:", error);
    return null;
  }
}

function PaymentSuccess() {
  const { paymentHashID } = useParams();
  const [paymentData] = useState(readPaymentData);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState("");

  const payment = paymentData?.payment;
  const customer = paymentData?.customer;
  const order = paymentData?.order;
  const cartToken = paymentData?.cartToken ?? payment?.cart_token;
  const orderId = order?.id
    ?? order?.ID
    ?? order?.order_id
    ?? paymentData?.orderId
    ?? paymentData?.order_id
    ?? payment?.order_id
    ?? payment?.orderId;

  useEffect(() => {
    const items = order?.items ?? [];

    if (!cartToken || !orderId) return;

    const transactionKey = `inventoryTransaction:${cartToken}:${orderId}`;
    if (sessionStorage.getItem(transactionKey)) return;

    sessionStorage.setItem(transactionKey, "pending");
    createInventoryTransaction(cartToken, {
      items,
      customer_id: customer?.id ?? 0,
      order_id: orderId,
    })
      .then(() => sessionStorage.setItem(transactionKey, "completed"))
      .catch((error) => {
        sessionStorage.removeItem(transactionKey);
        console.error("Failed to create inventory transaction:", error);
      });
  }, [cartToken, customer?.id, order?.items, orderId]);

  const handleKeepReceipt = async () => {
    if (!orderId) {
      setReceiptError("A receipt is not available for this confirmation.");
      return;
    }

    setReceiptLoading(true);
    setReceiptError("");

    try {
      const receiptBlob = await getOrderReceipt(orderId);
      const receiptUrl = URL.createObjectURL(receiptBlob);
      const receiptLink = document.createElement("a");
      receiptLink.href = receiptUrl;
      receiptLink.download = `m80-receipt-${orderId}.pdf`;
      document.body.appendChild(receiptLink);
      receiptLink.click();
      receiptLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(receiptUrl), 1000);
    } catch (error) {
      console.error("Failed to retrieve order receipt:", error);
      setReceiptError("We could not save your receipt. Please try again.");
    } finally {
      setReceiptLoading(false);
    }
  };

  let shippingAddress = null;
  try {
    const snapshot = customer?.shipping_address_snapshot;
    shippingAddress = typeof snapshot === "string" ? JSON.parse(snapshot) : snapshot;
  } catch (error) {
    console.error("Failed to parse shipping address:", error);
  }

  const customerName = customer?.name
    || [shippingAddress?.firstName, shippingAddress?.lastName].filter(Boolean).join(" ")
    || "Valued Customer";
  const items = order?.items ?? [];

  return (
    <Page title="Thank You" skinny={true} pageClassesOverride={pageClasses}>
      <section className={styles.confirmation}>
        <div className={styles.hero}>
          <span className={styles.statusIcon} aria-hidden="true"><LuCheck /></span>
          <div>
            <p className={styles.eyebrow}>Payment confirmed</p>
            <h2>Thank you, {customerName}.</h2>
            <p>Your order is confirmed. Keep your receipt for your records.</p>
          </div>
        </div>

        <div className={styles.referenceBar}>
          <span>Order</span>
          <strong>{orderId || paymentHashID || "Confirmed"}</strong>
        </div>

        {paymentData ? (
          <div className={styles.detailsGrid}>
            <section className={styles.panel}>
              <h3>Order details</h3>
              <div className={styles.itemList}>
                {items.length > 0 ? items.map((item, index) => (
                  <div className={styles.item} key={item.id ?? `${item.product_name}-${index}`}>
                    <div>
                      <strong>{item.product_name || item.name || "M80 item"}</strong>
                      <span>Quantity {item.quantity || 1}</span>
                    </div>
                    <span>{formatCurrency(item.price_cents ?? item.unit_price_cents)}</span>
                  </div>
                )) : <p className={styles.muted}>Order items are listed on your receipt.</p>}
              </div>
              <dl className={styles.totals}>
                <div><dt>Subtotal</dt><dd>{formatCurrency(payment?.subtotal_cents)}</dd></div>
                <div><dt>Shipping</dt><dd>{formatCurrency(payment?.shipping_cents)}</dd></div>
                <div className={styles.total}><dt>Total paid</dt><dd>{formatCurrency(payment?.total_cents)}</dd></div>
              </dl>
            </section>

            <section className={styles.panel}>
              <h3>Delivery</h3>
              <address>
                {shippingAddress ? (
                  <>
                    <strong>{shippingAddress.firstName} {shippingAddress.lastName}</strong>
                    <span>{shippingAddress.address}</span>
                    <span>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}</span>
                  </>
                ) : <span>Delivery details are included on your receipt.</span>}
                {customer?.email && <span>{customer.email}</span>}
                {customer?.phone && <span>{customer.phone}</span>}
              </address>
            </section>
          </div>
        ) : (
          <p className={styles.sessionNotice}>This payment is confirmed, but the order details are no longer available in this browser session.</p>
        )}

        <div className={styles.actions}>
          <button type="button" onClick={handleKeepReceipt} disabled={receiptLoading || !orderId}>
            <LuDownload aria-hidden="true" />
            {receiptLoading ? "Preparing receipt" : "Keep receipt"}
          </button>
          <Link to="/home#top">Return to collection</Link>
        </div>
        {receiptError && <p className={styles.error} role="alert">{receiptError}</p>}
      </section>
    </Page>
  );
}

export default PaymentSuccess;